import React, {FC, useCallback} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Dropdown} from '@optimaxdev/design-system';
import {Approve, Icon} from '@optimaxdev/icons';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {Feature} from 'components/feature';
import {CART_BUTTON} from 'constants/analytics/events/cart';
import {
    updateCartItem,
    updateCartItemSubscription,
} from 'desktop/pages/cart/store/reducers/changeItemQty/changeItemQty';
import {getIsUpdateItemPending} from 'desktop/pages/cart/store/selectors';
import {AddToCartType} from 'desktop/pages/cart/types/quoteItem';
import {ApplicationStoreType} from 'desktop/store';
import {useSubscriptionPlansRequest} from 'mobile/features/cLSubscription/hooks/useSubscriptionPlans/useSubscriptionPlans';

import {CartItemPopup} from '../cartItemPopup/cartItemPopup';
import {CartItemPopupRedesign} from '../cartItemPopupRedesign/cartItemPopup';

import {CartSubscriptionOfferCheckbox} from './cartSubscriptionOfferCheckbox/cartSubscriptionOfferCheckbox';

import s from './cartSubscriptionOffer.scss';

export type PropsType = {
    productId: string;
    quoteItemId: string;
    currentSubscriptionPlan: string;
    subscriptionMode: boolean;
    updateItem: (data: {data: AddToCartType; quoteType?: string}) => void;
    updateItemSubscription: (data: AddToCartType) => void;
    isUpdateItemPending: boolean;
};

type DropdownPlanType = {value: string; label: string; boxesPerEye: number};

const dropdownStyles = {container: s.dropdownCart};

/**
 * Sends analytics event with desired label
 * @param {string} label - label for the event
 */
const sendAnalyticsEvent = (label: string) => {
    sendDesktopAnalyticsEvent(CART_BUTTON, {
        action: 'CL Subscription',
        label,
    });
};

/**
 * Adds 'Supply every' to the beginning of every plan name
 * @param {DropdownPlanType} plans - plans
 * @returns {DropdownPlanType} - adapted plans
 */
const getPlansForDropdown = (plans: DropdownPlanType[]): DropdownPlanType[] =>
    plans.map(({label, ...plan}) => ({
        ...plan,
        label: `Supply every ${label.toLowerCase()}`,
    }));

/**
 * Subscription offer component for the cart item
 * @returns {React.ReactElement} - component
 */
export const CartSubscriptionOffer: FC<PropsType> = ({
    currentSubscriptionPlan,
    isUpdateItemPending,
    productId,
    quoteItemId,
    subscriptionMode,
    updateItem,
    updateItemSubscription,
}) => {
    const {
        data: plans = [],
        isError,
        isInitialLoading: isPlansLoading,
    } = useSubscriptionPlansRequest(productId);
    const setSubscriptionMode = useCallback(() => {
        /*
            When the user clicks the checkbox we should select the first value from the dropdown
            if there is no one
         */
        if (!subscriptionMode && !currentSubscriptionPlan && plans) {
            updateItemSubscription({
                currentSubscriptionPlan: plans[0].value,
                quoteItemId,
                subscriptionMode: !subscriptionMode,
            });

            return;
        }

        sendAnalyticsEvent(subscriptionMode ? 'uncheck' : 'check');

        updateItemSubscription({quoteItemId, subscriptionMode: !subscriptionMode});
    }, [updateItemSubscription, quoteItemId, subscriptionMode, currentSubscriptionPlan, plans]);

    const selectPlan = useCallback(
        (value: React.ReactNode) => {
            updateItem({data: {currentSubscriptionPlan: String(value), quoteItemId}});
        },
        [updateItem, quoteItemId],
    );

    const isCheckboxDisabled = isUpdateItemPending || isPlansLoading;
    const isDropDownDisabled =
        !subscriptionMode || isUpdateItemPending || isPlansLoading || plans.length < 2;

    const plansForDropdown = getPlansForDropdown(plans);

    if (isError || !plans.length) return null;

    return (
        <aside className={classNames(s.container, s.redesign)}>
            <div className={s.topSection}>
                <CartSubscriptionOfferCheckbox
                    isDisabled={isCheckboxDisabled}
                    setSubscriptionMode={setSubscriptionMode}
                    subscriptionMode={subscriptionMode}
                />
                <Dropdown
                    classes={dropdownStyles}
                    disabled={isDropDownDisabled}
                    onClick={selectPlan}
                    options={plansForDropdown}
                    selectedValue={currentSubscriptionPlan}
                    size="small"
                />
            </div>
            <div className={s.bottomSection}>
                <div className={s.row}>
                    <Icon className={s.check} icon={Approve} strokeWidth={1.5} />
                    <p className={s.discount}>Free glasses, worth up to $150.</p>
                </div>
                <div className={s.row}>
                    <Icon className={s.check} icon={Approve} strokeWidth={1.5} />
                    <p className={s.discount}>
                        15% off any future order.{' '}
                        <Feature
                            fallback={
                                <CartItemPopup
                                    onPopupOpen={() => sendAnalyticsEvent('Learn more')}
                                />
                            }
                            flag="clCartLearnMore"
                            platform="desktop"
                        >
                            <CartItemPopupRedesign
                                onPopupOpen={() => sendAnalyticsEvent('Learn more')}
                            />
                        </Feature>
                    </p>
                </div>
            </div>
        </aside>
    );
};

const mapStateToProps = (store: ApplicationStoreType) => ({
    isUpdateItemPending: getIsUpdateItemPending(store),
});

const mapDispatchToProps = {
    updateItem: updateCartItem,
    updateItemSubscription: updateCartItemSubscription,
};

export const CartSubscriptionOfferConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(CartSubscriptionOffer);
